import type { FC, ReactNode } from 'react'

import { cn } from '@/lib/utils'
import { transliterate } from '@/prakrit/lib/grammar/translit'
import { PrakritText } from '@/prakrit/components/common/prakrit-word'
import { AudioButton } from '@/prakrit/components/common/audio-button'
import { StatusBadge } from '@/prakrit/components/common/status-badge'

export type TriSize = 'sm' | 'md' | 'lg' | 'xl' | 'display'

interface TriLineProps {
    /** The Prakrit line (every word becomes tappable). */
    pra: string
    hi?: string
    en?: string
    /** Defaults to the engine's IAST transliteration; shown only when the transliteration toggle is on. */
    translit?: string
    size?: TriSize
    align?: 'left' | 'center'
    audio?: boolean
    highlight?: string[]
    /** Not yet approved by a teacher: show the समीक्षाधीन badge. */
    unreviewed?: boolean
    plain?: boolean
    className?: string
    as?: 'div' | 'li'
}

const SIZE: Record<TriSize, string> = {
    sm: 'pra-sm',
    md: 'pra-md',
    lg: 'pra-lg',
    xl: 'pra-xl',
    display: 'pra-display'
}

/**
 * Prakrit → Hindi · English → transliteration → audio.
 * The explanation-language setting hides Hindi or English; Prakrit is always shown.
 */
export const TriLine: FC<TriLineProps> = ({
    pra,
    hi,
    en,
    translit,
    size = 'lg',
    align = 'left',
    audio = true,
    highlight,
    unreviewed = false,
    plain = false,
    className,
    as = 'div'
}): ReactNode => {
    const Tag = as
    const centered = align === 'center'

    return (
        <Tag className={cn('flex flex-col gap-1', centered && 'items-center text-center', className)}>
            <p className={cn('pra', SIZE[size])}>
                <PrakritText text={pra} highlight={highlight} plain={plain} />
                {unreviewed && <StatusBadge kind='needs_review' className='ml-2 align-middle' />}
            </p>
            {(hi || en) && (
                <p className={cn('flex flex-col gap-x-2 text-[0.9375rem] sm:flex-row sm:flex-wrap sm:items-baseline', centered && 'items-center sm:justify-center')}>
                    {hi && <span lang='hi' data-ex='hi' className='hindi text-foreground'>{hi}</span>}
                    {hi && en && <span aria-hidden='true' className='ex-sep hidden text-muted-foreground/60 sm:inline'>·</span>}
                    {en && <span lang='en' data-ex='en' className='text-muted-foreground'>{en}</span>}
                </p>
            )}
            <p lang='pra-Latn' className='translit text-sm italic text-muted-foreground'>{translit ?? transliterate(pra)}</p>
            {audio && <AudioButton text={pra} size='sm' className='mt-1' />}
        </Tag>
    )
}
